import {
  Text,
  TextLink,
  Alert,
  PageBlock,
  Box,
  Dialog,
  Stack,
  Columns,
  Column,
  TextLinkButton,
} from 'braid-design-system';
import { get as localStorageGet, set as localStorageSet } from 'local-storage';

import { useState, useEffect } from 'react';
import { useTranslations } from '@vocab/react';
import translations from './.vocab';
import useOnboardingBannerContent from './useOnboardingBannerContent';
import * as styles from './OnboardingBanner.css';
import { useSelector } from 'react-redux';
import { selectFeatureFlag } from 'src/store/selectors';
import { useAnalyticsFacade } from 'src/modules/AnalyticsFacade';
import { useBrandName } from 'src/hooks/useBrandName';

export const OnboardingBanner = () => {
  const { t } = useTranslations(translations);
  const props = useOnboardingBannerContent();
  const analyticsFacade = useAnalyticsFacade();
  const bannerOverride = useSelector(
    selectFeatureFlag('displayCandidateOnboardingBanner'),
  );

  const [displayOnboardingBanner, setDisplayOnboardingBanner] = useState(false);
  const [displayOnboardingInfo, setDisplayOnboardingInfo] = useState(false);

  const brandName = useBrandName();

  const displayBanner =
    localStorageGet<boolean>('displayOnboardingBanner') ?? true;

  useEffect(() => {
    if (displayBanner) {
      setDisplayOnboardingBanner(true);
    }
  }, [displayOnboardingBanner, displayBanner]);

  const handleDismiss = () => {
    setDisplayOnboardingBanner(!displayOnboardingBanner);
    localStorageSet('displayOnboardingBanner', !displayOnboardingBanner);
    analyticsFacade.onboardingExperienceAlertDimissPressed();
  };

  const dialogToggleAnalytics = () =>
    displayOnboardingInfo
      ? analyticsFacade.onboardingExperienceDismissPressed()
      : analyticsFacade.onboardingExperienceAlertPressed();

  const toggleBannerDialog = () => {
    dialogToggleAnalytics();
    setDisplayOnboardingInfo(!displayOnboardingInfo);
  };

  const displayCandidateOnboardingBanner =
    (bannerOverride && displayOnboardingBanner) ||
    (props.isCandidateOnboardingBannerValid && displayOnboardingBanner);

  return displayCandidateOnboardingBanner ? (
    <PageBlock component="section">
      <Box
        aria-labelledby="OnboardingBanner"
        data={{
          automation: 'candidateOnboardingBanner',
        }}
        paddingTop="xxxlarge"
      >
        <Alert
          tone="promote"
          onClose={handleDismiss}
          closeLabel="Close what's new alert"
        >
          <Text>
            {t('Finding the right job just got easier')}{' '}
            <TextLinkButton onClick={toggleBannerDialog}>
              {t("See what's new on { brand }", { brand: brandName })}
            </TextLinkButton>
          </Text>
        </Alert>
      </Box>
      <Dialog
        id="onboardingBannerDialog"
        title={t('Finding the right job just got easier')}
        open={displayOnboardingInfo}
        onClose={toggleBannerDialog}
        width="xsmall"
        data={{
          automation: 'candidateOnboardingBannerDialog',
        }}
      >
        <Stack space="gutter" align="center">
          {props.bannerContent.map((row, index) =>
            onboardingBannerItem(row, index),
          )}
        </Stack>
        {props?.externalHelpURL ? (
          <Text weight="strong">
            <TextLink
              target="_blank"
              href={props?.externalHelpURL}
              onClick={() => analyticsFacade.onboardingExperiencePressed()}
            >
              {t('See what else is new')}
            </TextLink>
          </Text>
        ) : null}
      </Dialog>
    </PageBlock>
  ) : null;
};

const onboardingBannerItem = (
  row: {
    description: string;
    img: string;
  },
  index: number,
) => (
  <Columns space="medium" align="center" alignY="center" key={index}>
    <Column width="content">
      <Box
        component="img"
        className={styles.OnboardingImageAsset}
        src={row.img}
      />
    </Column>
    <Column>
      <Text>{row.description}</Text>
    </Column>
  </Columns>
);

export default OnboardingBanner;
